// Shared helpers for the embedded Stripe Elements Payment Element used by
// BillingPage.js (subscription checkout) and TokenUsagePage.js (token top-ups).
// Mirrors the pattern already built in eva-gtm-platform's CRM backend
// (apps/crm/backend/routes/order_forms.py's buildAppearance()/showPaymentElement()):
// a Payment Element mounted directly on our own page instead of a redirect to
// Stripe's hosted Checkout, themed via the Appearance API to match this app's design
// tokens (frontend/tailwind.config.js, frontend/src/index.css).
import { loadStripe } from '@stripe/stripe-js';

let stripePromise = null;

// loadStripe() is meant to be called once per publishable key and reused -- calling
// it repeatedly leaks <script> tags and Stripe instances.
export function getStripe() {
  if (!stripePromise) {
    const key = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
    if (!key) {
      console.error('REACT_APP_STRIPE_PUBLISHABLE_KEY is not set; Stripe Elements cannot mount.');
      return null;
    }
    stripePromise = loadStripe(key);
  }
  return stripePromise;
}

// Reads this app's live theme tokens (frontend/src/index.css) at mount time so the
// Payment Element matches per-tenant brand overrides (ThemeContext) when present.
// Falls back to the hardcoded Eva purple/dark pair if a token is missing -- the
// per-tenant override system isn't otherwise wired into this helper.
export function buildStripeAppearance() {
  const isDark = document.documentElement.classList.contains('dark');
  const style = getComputedStyle(document.documentElement);
  const cssVar = (name, fallback) => {
    const value = style.getPropertyValue(name)?.trim();
    return value ? `hsl(${value})` : fallback;
  };

  return {
    theme: isDark ? 'night' : 'stripe',
    variables: {
      colorPrimary: cssVar('--primary', isDark ? 'hsl(263, 70%, 60%)' : 'hsl(262, 83%, 58%)'),
      colorBackground: cssVar('--card', isDark ? 'hsl(217, 33%, 17%)' : 'hsl(0, 0%, 100%)'),
      colorText: cssVar('--foreground', isDark ? 'hsl(210, 40%, 98%)' : 'hsl(222, 47%, 11%)'),
      colorDanger: cssVar('--destructive', isDark ? 'hsl(0, 62.8%, 50%)' : 'hsl(0, 84.2%, 60.2%)'),
      fontFamily: 'IBM Plex Sans, system-ui, sans-serif',
      borderRadius: '12px',
    },
  };
}
